/**
 * Reinigen List Page
 *
 * Shows all items awaiting reinigen (cleaning).
 * Items are filtered by: Status__c = 'Drogen'
 *
 * Features:
 * - Search by item number (WI-xxxxx), work order, or product
 * - Click item to go to cleaning form
 * - Shows progress badge
 * - Caches items in IndexedDB for offline support
 * - Polls every 60 seconds for real-time updates
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { WbButton, WbChip, WbFlag, WbListRow, WbSearchField, WbSectionKop, WbState } from '../components/wb';
import { useHeader } from '../stores';
import { WachtrijMislukt } from '../components/WachtrijMislukt';
import { salesforceClient } from '../lib/salesforce';
import {
  getCachedReinigenItems,
  cacheReinigenItems,
  type CachedReinigenItem,
} from '../lib/db/database';
import type { ReinigenItem } from '../lib/salesforce';
import { matchtZoek } from '../lib/utils/zoek';

// Polling interval for real-time updates (60 seconds)
const POLLING_INTERVAL_MS = 60000;

export function ReinigenListPage() {
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [items, setItems] = useState<ReinigenItem[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // Fetch items from API or cache
  const fetchItems = useCallback(async (showRefreshSpinner = false) => {
    try {
      if (showRefreshSpinner) {
        setIsRefreshing(true);
      } else {
        setIsLoading(true);
      }
      setError(null);

      // Try API first
      const response = await salesforceClient.getReinigenItems();
      console.log('[ReinigenListPage] API response:', response);

      if (response.success && response.data) {
        const apiItems = response.data.items || [];
        setItems(apiItems);

        // Cache items for offline use
        const itemsToCache: CachedReinigenItem[] = apiItems.map(item => ({
          itemId: item.itemId,
          itemName: item.itemName,
          productName: item.productName,
          werkOrderId: item.werkOrderId,
          werkOrderName: item.werkOrderName,
          companyCamProjectId: item.companyCamProjectId,
          lengthCm: item.lengthCm,
          widthCm: item.widthCm,
          diameterCm: item.diameterCm,
          surfaceM2: item.surfaceM2,
          vorm: item.vorm,
          voorinspectie: item.voorinspectie,
          voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
          wasrapport: item.wasrapport,
          hoeReinigen: item.hoeReinigen,
          secondaryItems: item.secondaryItems,
          isKlacht: item.isKlacht,
          klachtReden: item.klachtReden,
          _cachedAt: new Date(),
        }));
        await cacheReinigenItems(itemsToCache);
      } else {
        // Try cache on API failure
        console.warn('[ReinigenListPage] API failed, trying cache');
        const cachedItems = await getCachedReinigenItems();
        if (cachedItems.length > 0) {
          const mappedItems: ReinigenItem[] = cachedItems.map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            productName: item.productName,
            werkOrderId: item.werkOrderId,
            werkOrderName: item.werkOrderName,
            companyCamProjectId: item.companyCamProjectId,
            lengthCm: item.lengthCm,
            widthCm: item.widthCm,
            diameterCm: item.diameterCm,
            surfaceM2: item.surfaceM2,
            vorm: item.vorm,
            voorinspectie: item.voorinspectie,
            voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
            wasrapport: item.wasrapport,
            hoeReinigen: item.hoeReinigen,
            secondaryItems: item.secondaryItems,
            isKlacht: item.isKlacht,
            klachtReden: item.klachtReden,
          }));
          setItems(mappedItems);
        } else {
          setError(response.message || 'Kon items niet ophalen');
          setItems([]);
        }
      }
    } catch (err) {
      console.error('[ReinigenListPage] Error fetching items:', err);

      // Try cache on network error
      try {
        const cachedItems = await getCachedReinigenItems();
        if (cachedItems.length > 0) {
          const mappedItems: ReinigenItem[] = cachedItems.map(item => ({
            itemId: item.itemId,
            itemName: item.itemName,
            productName: item.productName,
            werkOrderId: item.werkOrderId,
            werkOrderName: item.werkOrderName,
            companyCamProjectId: item.companyCamProjectId,
            lengthCm: item.lengthCm,
            widthCm: item.widthCm,
            diameterCm: item.diameterCm,
            surfaceM2: item.surfaceM2,
            vorm: item.vorm,
            voorinspectie: item.voorinspectie,
            voorinspectieOpmerkingen: item.voorinspectieOpmerkingen,
            wasrapport: item.wasrapport,
            hoeReinigen: item.hoeReinigen,
            secondaryItems: item.secondaryItems,
            isKlacht: item.isKlacht,
            klachtReden: item.klachtReden,
          }));
          setItems(mappedItems);
          setError('Offline modus - gegevens kunnen verouderd zijn');
        } else {
          setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
          setItems([]);
        }
      } catch {
        setError(err instanceof Error ? err.message : 'Er ging iets mis bij het laden');
        setItems([]);
      }
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  }, []);

  // Fetch items on mount
  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  // Polling for real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      fetchItems(true);
    }, POLLING_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [fetchItems]);

  // Filter items by search query
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) {
      return items;
    }

    return items.filter(item => matchtZoek(searchQuery, item.itemName, item.werkOrderName, item.productName));
  }, [items, searchQuery]);

  // Handle refresh
  const handleRefresh = async () => {
    await fetchItems(true);
  };

  const maatVan = (item: ReinigenItem) => {
    if (item.lengthCm && item.widthCm) return `${item.lengthCm} × ${item.widthCm}`;
    if (item.diameterCm) return `Ø ${item.diameterCm}`;
    if (item.surfaceM2) return `${item.surfaceM2.toFixed(1)} m²`;
    return 'maat onbekend';
  };

  useHeader('Reinigen', `${items.length}`);

  return (
    <div className="space-y-4">
      <WbSearchField value={searchQuery} onChange={setSearchQuery} placeholder="Zoek WI-nummer, order of product" />
      <WachtrijMislukt soort="reinigen" />

      {isLoading && <WbState variant="laden" titel="Items laden…" />}
      {!isLoading && error && items.length === 0 && <WbState variant="fout" titel={error} onOpnieuw={() => fetchItems()} />}
      {!isLoading && error && items.length > 0 && <WbFlag kind="opmerking">{error}</WbFlag>}
      {!isLoading && !error && items.length === 0 && <WbState variant="leeg" titel="Alles gereinigd." tekst="Er zijn geen kleden die gereinigd moeten worden." />}
      {!isLoading && !error && items.length > 0 && filteredItems.length === 0 && (
        <WbState variant="leeg" titel={`Geen resultaten voor "${searchQuery}" in deze lijst.`}>
          <Link to={`/zoek?q=${encodeURIComponent(searchQuery)}`} className="wb-focus inline-flex min-h-tap items-center font-semibold underline underline-offset-4 text-wb-inkt">
            Zoek "{searchQuery}" in alle kleden
          </Link>
        </WbState>
      )}

      {!isLoading && filteredItems.length > 0 && (
        <section className="space-y-1.5">
          <WbSectionKop
            label="Te reinigen"
            teller={filteredItems.length}
            actie={
              <WbButton variant="link" onClick={handleRefresh} disabled={isRefreshing}>
                {isRefreshing ? 'Verversen…' : 'Ververs'}
              </WbButton>
            }
          />
          {filteredItems.map((item) => (
            <WbListRow
              key={item.itemId}
              onClick={() => navigate(`/reinigen/${item.itemId}`)}
              hoofd={item.itemName}
              chips={
                <>
                  {item.isKlacht && <WbChip kind="klacht">Klacht</WbChip>}
                  {item.hoeReinigen && <WbChip kind="neutraal">{item.hoeReinigen}</WbChip>}
                </>
              }
              sub={`${maatVan(item)} · ${item.werkOrderName} · ${item.productName}`}
            />
          ))}
        </section>
      )}
    </div>
  );
}
